import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  FileText,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Loader2,
  Search,
  Save,
  Trash2,
  X,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  Filter,
  BookOpen,
  Sparkles,
} from 'lucide-react';
import {
  docProcessingApi,
  DocumentInfo,
  FrontmatterEntry,
  FrontmatterUpsertRequest,
  BulkGenerateEvent,
} from '../../services/docProcessingApi';

type FilterMode = 'all' | 'with' | 'without';
type SortField = 'filename' | 'category' | 'frontmatter' | 'summary';
type SortDir = 'asc' | 'desc';

const PAGE_SIZE = 50;
const MAX_BULK_SELECT = 50;

interface DocWithFrontmatter extends DocumentInfo {
  frontmatter?: FrontmatterEntry | null;
}

export default function FrontmatterEditor() {
  // Document + frontmatter data
  const [documents, setDocuments] = useState<DocWithFrontmatter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Filter, search & sort
  const [filterMode, setFilterMode] = useState<FilterMode>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [sortField, setSortField] = useState<SortField>('filename');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  // Column filters
  const [colFilterFilename, setColFilterFilename] = useState('');
  const [colFilterCategory, setColFilterCategory] = useState('');

  // Selection for bulk generation
  const [selectedDocs, setSelectedDocs] = useState<Set<string>>(new Set());

  // Bulk generation state
  const [generating, setGenerating] = useState(false);
  const [genProgress, setGenProgress] = useState<{ done: number; total: number; current: string }>({ done: 0, total: 0, current: '' });
  const [genErrors, setGenErrors] = useState<string[]>([]);
  // Dry-run previews — rows are appended as `completed` events stream in.
  // Cleared when a fresh bulk-generate or dry-run starts. The panel stays
  // visible after the run so the analyst can compare old vs new before
  // re-running for real.
  const [dryRunPreviews, setDryRunPreviews] = useState<Array<{
    filename: string;
    previous: { summary: string; module: string | null; feature: string | null } | null;
    next: { summary: string; module: string | null; feature: string | null };
  }>>([]);

  // Editor state
  const [editingDoc, setEditingDoc] = useState<DocWithFrontmatter | null>(null);
  const [formSummary, setFormSummary] = useState('');
  const [formModule, setFormModule] = useState('');
  const [formFeature, setFormFeature] = useState('');
  const [saving, setSaving] = useState(false);

  // Stats
  const totalDocs = documents.length;
  const withFrontmatter = documents.filter((d) => d.frontmatter).length;
  const withoutFrontmatter = totalDocs - withFrontmatter;

  // Load documents and frontmatter
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [docsResult, fmResult] = await Promise.all([
        docProcessingApi.listDocuments(),
        docProcessingApi.listFrontmatter(),
      ]);

      // Build frontmatter lookup by gcs_path and filename
      const fmByPath: Record<string, FrontmatterEntry> = {};
      const fmByFilename: Record<string, FrontmatterEntry> = {};
      for (const entry of fmResult.entries) {
        if (entry.gcs_path) fmByPath[entry.gcs_path] = entry;
        if (entry.filename) fmByFilename[entry.filename] = entry;
      }

      // Merge
      const merged: DocWithFrontmatter[] = (docsResult.documents || []).map((doc) => ({
        ...doc,
        frontmatter: (doc.gcs_path && fmByPath[doc.gcs_path]) || fmByFilename[doc.filename] || null,
      }));

      setDocuments(merged);
    } catch (err: any) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filtered + searched + sorted list
  const filteredDocs = useMemo(() => {
    let list = documents;

    if (filterMode === 'with') {
      list = list.filter((d) => d.frontmatter);
    } else if (filterMode === 'without') {
      list = list.filter((d) => !d.frontmatter);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (d) =>
          d.filename.toLowerCase().includes(q) ||
          d.category?.toLowerCase().includes(q) ||
          d.frontmatter?.summary?.toLowerCase().includes(q) ||
          d.frontmatter?.module?.toLowerCase().includes(q)
      );
    }

    // Column-level filters
    if (colFilterFilename.trim()) {
      const q = colFilterFilename.toLowerCase();
      list = list.filter((d) => d.filename.toLowerCase().includes(q));
    }
    if (colFilterCategory.trim()) {
      const q = colFilterCategory.toLowerCase();
      list = list.filter((d) => (d.category || '').toLowerCase().includes(q));
    }

    // Sort
    list = [...list].sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'filename':
          cmp = a.filename.localeCompare(b.filename);
          break;
        case 'category':
          cmp = (a.category || '').localeCompare(b.category || '');
          break;
        case 'frontmatter':
          cmp = (a.frontmatter ? 1 : 0) - (b.frontmatter ? 1 : 0);
          break;
        case 'summary':
          cmp = (a.frontmatter?.summary || '').localeCompare(b.frontmatter?.summary || '');
          break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });

    return list;
  }, [documents, filterMode, searchQuery, colFilterFilename, colFilterCategory, sortField, sortDir]);

  // Pagination
  const totalPages = Math.ceil(filteredDocs.length / PAGE_SIZE);
  const paginatedDocs = useMemo(
    () => filteredDocs.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE),
    [filteredDocs, currentPage]
  );

  useEffect(() => {
    setCurrentPage(0);
  }, [filterMode, searchQuery, colFilterFilename, colFilterCategory]);

  // Sort toggle
  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ChevronUp size={10} className="text-gray-300" />;
    return sortDir === 'asc'
      ? <ChevronUp size={10} className="text-blue-600" />
      : <ChevronDown size={10} className="text-blue-600" />;
  };

  // Selection helpers
  const docKey = (d: DocWithFrontmatter) =>
    d.gcs_path ?? `${d.category}-${d.filename}`;

  const toggleDoc = (key: string) => {
    setSelectedDocs((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        if (next.size >= MAX_BULK_SELECT) return prev; // enforce limit
        next.add(key);
      }
      return next;
    });
  };

  const toggleAllOnPage = () => {
    const pageKeys = paginatedDocs.map(docKey);
    const allSelected = pageKeys.every((k) => selectedDocs.has(k));
    setSelectedDocs((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        pageKeys.forEach((k) => next.delete(k));
      } else {
        for (const k of pageKeys) {
          if (next.size >= MAX_BULK_SELECT) break;
          next.add(k);
        }
      }
      return next;
    });
  };

  // Bulk AI generation. When `dryRun` is true, results are streamed into the
  // dry-run preview panel and nothing is written to the database — used to
  // sanity-check the prompt on a small sample before re-running on the full
  // selection for real.
  const handleBulkGenerate = async (dryRun: boolean = false) => {
    if (selectedDocs.size === 0) return;

    const selectedList = documents.filter((d) => selectedDocs.has(docKey(d)));
    const docsToGenerate = selectedList.map((d) => ({
      gcs_path: d.gcs_path || `docs/${d.category}/${d.filename}`,
      filename: d.filename,
      category: d.category || null,
    }));

    const confirmMsg = dryRun
      ? `Dry-run AI frontmatter for ${docsToGenerate.length} document(s)?\n\nProposed values will be shown side-by-side with the current values. Nothing will be written to the database.`
      : `Generate AI frontmatter for ${docsToGenerate.length} document(s) using Claude?\n\nThis will read existing chunks from the database and create summaries. Documents must be ingested first.`;
    if (!window.confirm(confirmMsg)) return;

    setGenerating(true);
    setGenProgress({ done: 0, total: docsToGenerate.length, current: '' });
    setGenErrors([]);
    setDryRunPreviews([]);
    setError(null);
    setSuccess(null);

    try {
      await docProcessingApi.generateFrontmatterBulk(docsToGenerate, (event: BulkGenerateEvent) => {
        if (event.status === 'complete') {
          setGenProgress((p) => ({ ...p, done: p.total, current: '' }));
          setSuccess(
            dryRun
              ? `Dry-run complete: ${event.succeeded} previewed, ${event.failed} failed. Nothing was written.`
              : `Generated frontmatter: ${event.succeeded} succeeded, ${event.failed} failed.`,
          );
          return;
        }

        if (event.status === 'completed') {
          setGenProgress((p) => ({ ...p, done: p.done + 1, current: '' }));
          if (dryRun && event.result) {
            setDryRunPreviews((prev) => [
              ...prev,
              {
                filename: event.filename || event.result!.filename,
                previous: event.previous ?? null,
                next: {
                  summary: event.result!.summary,
                  module: event.result!.module ?? null,
                  feature: event.result!.feature ?? null,
                },
              },
            ]);
          }
        } else if (event.status === 'in_progress') {
          setGenProgress((p) => ({ ...p, current: event.filename || '' }));
        } else if (event.status === 'error') {
          setGenProgress((p) => ({ ...p, done: p.done + 1, current: '' }));
          setGenErrors((prev) => [...prev, `${event.filename}: ${event.error}`]);
        }
      }, dryRun);
    } catch (err: any) {
      setError(err.message || (dryRun ? 'Dry-run failed' : 'Bulk generation failed'));
    } finally {
      setGenerating(false);
      if (!dryRun) {
        // Keep the selection on dry-run so the analyst can re-run for real
        // without re-picking the same 10 documents. Also skip the refetch —
        // nothing was written.
        setSelectedDocs(new Set());
        await loadData();
      }
    }
  };

  // Single-document AI generation (from editor modal)
  const [generatingSingle, setGeneratingSingle] = useState(false);

  const handleSingleGenerate = async (doc: DocWithFrontmatter) => {
    if (!window.confirm(
      `Generate AI frontmatter for "${doc.filename}" using Claude?\n\nThis will read existing chunks and create a summary. The document must be ingested first.`
    )) return;

    setGeneratingSingle(true);
    setError(null);
    setSuccess(null);

    const docPayload = {
      gcs_path: doc.gcs_path || `docs/${doc.category}/${doc.filename}`,
      filename: doc.filename,
      category: doc.category || null,
    };

    try {
      await docProcessingApi.generateFrontmatterBulk([docPayload], (event: BulkGenerateEvent) => {
        if (event.status === 'complete') {
          if (event.failed && event.failed > 0) {
            setError(`AI generation failed for "${doc.filename}".`);
          } else {
            setSuccess(`AI frontmatter generated for "${doc.filename}"`);
          }
        } else if (event.status === 'error') {
          setError(event.error || `AI generation failed for "${doc.filename}".`);
        } else if (event.status === 'completed' && event.result) {
          // Update form fields with the generated values
          setFormSummary(event.result.summary || '');
          setFormModule(event.result.module || '');
          setFormFeature(event.result.feature || '');
        }
      });
    } catch (err: any) {
      setError(err.message || 'AI generation failed');
    } finally {
      setGeneratingSingle(false);
      await loadData();
    }
  };

  // Open editor
  const openEditor = (doc: DocWithFrontmatter) => {
    setEditingDoc(doc);
    setFormSummary(doc.frontmatter?.summary || '');
    setFormModule(doc.frontmatter?.module || '');
    setFormFeature(doc.frontmatter?.feature || '');
    setError(null);
    setSuccess(null);
  };

  const closeEditor = () => {
    setEditingDoc(null);
    setFormSummary('');
    setFormModule('');
    setFormFeature('');
  };

  // Save frontmatter
  const handleSave = async () => {
    if (!editingDoc) return;
    if (formSummary.trim().length < 10) {
      setError('Summary must be at least 10 characters.');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const data: FrontmatterUpsertRequest = {
        gcs_path: editingDoc.gcs_path || `docs/${editingDoc.category}/${editingDoc.filename}`,
        filename: editingDoc.filename,
        summary: formSummary.trim(),
        category: editingDoc.category || null,
        module: formModule.trim() || null,
        feature: formFeature.trim() || null,
      };

      await docProcessingApi.upsertFrontmatter(data);
      setSuccess(`Frontmatter saved for "${editingDoc.filename}"`);
      closeEditor();
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to save frontmatter');
    } finally {
      setSaving(false);
    }
  };

  // Delete frontmatter
  const handleDelete = async () => {
    if (!editingDoc?.frontmatter) return;
    if (!window.confirm(`Remove frontmatter for "${editingDoc.filename}"?`)) return;

    setSaving(true);
    setError(null);

    try {
      const gcsPath = editingDoc.gcs_path || editingDoc.frontmatter.gcs_path;
      await docProcessingApi.deleteFrontmatter(gcsPath);
      setSuccess(`Frontmatter removed for "${editingDoc.filename}"`);
      closeEditor();
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Failed to delete frontmatter');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 flex items-start gap-2">
          <AlertTriangle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-red-800">Error</p>
            <p className="text-xs text-red-700 mt-0.5 break-words">{error}</p>
          </div>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600 flex-shrink-0">
            <XCircle size={14} />
          </button>
        </div>
      )}
      {success && !generating && (
        <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2 flex items-start gap-2">
          <CheckCircle size={16} className="text-green-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-green-700 flex-1">{success}</p>
          <button onClick={() => setSuccess(null)} className="text-green-400 hover:text-green-600 flex-shrink-0">
            <XCircle size={14} />
          </button>
        </div>
      )}

      {/* Stats bar */}
      <div className="bg-white rounded-lg border border-gray-200 px-4 py-2.5 flex items-center justify-between">
        <div className="flex items-center gap-5">
          <div className="flex items-center gap-1.5 text-xs text-gray-600">
            <FileText size={13} className="text-gray-400" />
            <span className="font-medium text-gray-900">{totalDocs}</span>
            Total Documents
          </div>
          <div className="flex items-center gap-1.5 text-xs text-gray-600">
            <CheckCircle size={13} className="text-green-500" />
            <span className="font-medium text-green-700">{withFrontmatter}</span>
            With Frontmatter
          </div>
          <div className="flex items-center gap-1.5 text-xs text-gray-600">
            <AlertTriangle size={13} className={withoutFrontmatter > 0 ? 'text-yellow-500' : 'text-gray-400'} />
            <span className={`font-medium ${withoutFrontmatter > 0 ? 'text-yellow-700' : 'text-gray-900'}`}>
              {withoutFrontmatter}
            </span>
            Missing
          </div>
        </div>
        {withFrontmatter > 0 && totalDocs > 0 && (
          <div className="text-xs text-gray-500">
            {Math.round((withFrontmatter / totalDocs) * 100)}% coverage
          </div>
        )}
      </div>

      {/* Filter + Search bar + Bulk actions */}
      <div className="bg-white rounded-lg border border-gray-200 px-4 py-2.5 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1.5">
          <Filter size={13} className="text-gray-400" />
          <div className="flex gap-1">
            {(['all', 'without', 'with'] as FilterMode[]).map((mode) => (
              <button
                key={mode}
                onClick={() => setFilterMode(mode)}
                className={`px-2 py-0.5 text-xs rounded transition-colors ${
                  filterMode === mode
                    ? 'bg-blue-100 text-blue-700 font-medium'
                    : 'text-gray-500 hover:bg-gray-100'
                }`}
              >
                {mode === 'all' ? 'All' : mode === 'without' ? 'Missing' : 'Has Frontmatter'}
              </button>
            ))}
          </div>
        </div>
        <div className="flex-1 relative">
          <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by filename, category, or summary..."
            className="w-full pl-7 pr-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        {/* Dry-run preview button — generates but does not write. */}
        <button
          onClick={() => handleBulkGenerate(true)}
          disabled={generating || selectedDocs.size === 0}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-white text-purple-700 border border-purple-300 rounded-md text-xs font-medium hover:bg-purple-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          title={selectedDocs.size === 0 ? 'Select documents first' : `Preview new frontmatter for ${selectedDocs.size} document(s) without writing`}
        >
          <Sparkles size={13} />
          Dry Run{selectedDocs.size > 0 ? ` (${selectedDocs.size})` : ''}
        </button>

        {/* Bulk AI generate button */}
        <button
          onClick={() => handleBulkGenerate(false)}
          disabled={generating || selectedDocs.size === 0}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 text-white rounded-md text-xs font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          title={selectedDocs.size === 0 ? 'Select documents first' : `Generate frontmatter for ${selectedDocs.size} document(s)`}
        >
          {generating ? (
            <Loader2 size={13} className="animate-spin" />
          ) : (
            <Sparkles size={13} />
          )}
          {generating
            ? `Generating (${genProgress.done}/${genProgress.total})...`
            : `AI Generate${selectedDocs.size > 0 ? ` (${selectedDocs.size})` : ''}`}
        </button>
        {selectedDocs.size > 0 && !generating && (
          <button
            onClick={() => setSelectedDocs(new Set())}
            className="text-xs text-gray-500 hover:text-gray-700"
          >
            Clear
          </button>
        )}
      </div>

      {/* Generation progress bar */}
      {generating && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg px-4 py-3">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-medium text-purple-800 flex items-center gap-1.5">
              <Sparkles size={12} />
              Generating frontmatter with Claude...
            </span>
            <span className="text-xs text-purple-600">
              {genProgress.done}/{genProgress.total}
            </span>
          </div>
          <div className="w-full bg-purple-200 rounded-full h-1.5">
            <div
              className="bg-purple-600 h-1.5 rounded-full transition-all duration-300"
              style={{ width: `${genProgress.total > 0 ? (genProgress.done / genProgress.total) * 100 : 0}%` }}
            />
          </div>
          {genProgress.current && (
            <p className="text-[10px] text-purple-600 mt-1 truncate">
              Processing: {genProgress.current}
            </p>
          )}
          {genErrors.length > 0 && (
            <div className="mt-2 space-y-0.5">
              {genErrors.slice(-3).map((e, i) => (
                <p key={i} className="text-[10px] text-red-600 truncate">{e}</p>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Dry-run preview — old vs new for each previewed document. */}
      {dryRunPreviews.length > 0 && (
        <div className="bg-white border border-purple-200 rounded-lg overflow-hidden">
          <div className="bg-purple-50 px-4 py-2 border-b border-purple-200 flex items-center justify-between">
            <h3 className="text-xs font-semibold text-purple-900 flex items-center gap-1.5">
              <Sparkles size={12} />
              Dry-run preview ({dryRunPreviews.length})
            </h3>
            <button
              onClick={() => setDryRunPreviews([])}
              className="text-xs text-purple-700 hover:text-purple-900"
            >
              Clear
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-gray-50 text-gray-700">
                <tr>
                  <th className="px-3 py-1.5 text-left font-semibold">Document</th>
                  <th className="px-3 py-1.5 text-left font-semibold">Field</th>
                  <th className="px-3 py-1.5 text-left font-semibold">Current</th>
                  <th className="px-3 py-1.5 text-left font-semibold">Proposed</th>
                </tr>
              </thead>
              <tbody>
                {dryRunPreviews.flatMap((row, i) => [
                  <tr key={`${i}-module`} className="border-t-2 border-gray-200 align-top">
                    <td className="px-3 py-2 font-medium text-gray-900" rowSpan={3}>
                      {row.filename}
                    </td>
                    <td className="px-3 py-1 text-gray-600">module</td>
                    <td className="px-3 py-1 text-gray-700">{row.previous?.module ?? <span className="text-gray-400 italic">(none)</span>}</td>
                    <td className="px-3 py-1 text-purple-800 font-medium">{row.next.module ?? <span className="text-gray-400 italic">(none)</span>}</td>
                  </tr>,
                  <tr key={`${i}-feature`} className="align-top">
                    <td className="px-3 py-1 text-gray-600">feature</td>
                    <td className="px-3 py-1 text-gray-700">{row.previous?.feature ?? <span className="text-gray-400 italic">(none)</span>}</td>
                    <td className="px-3 py-1 text-purple-800 font-medium">{row.next.feature ?? <span className="text-gray-400 italic">(none)</span>}</td>
                  </tr>,
                  <tr key={`${i}-summary`} className="align-top">
                    <td className="px-3 py-1 text-gray-600">summary</td>
                    <td className="px-3 py-1 text-gray-700 max-w-md">{row.previous?.summary || <span className="text-gray-400 italic">(none)</span>}</td>
                    <td className="px-3 py-1 text-purple-800 max-w-md">{row.next.summary}</td>
                  </tr>,
                ])}
              </tbody>
            </table>
          </div>
          <div className="bg-purple-50 px-4 py-2 border-t border-purple-200 text-[11px] text-purple-800">
            Nothing was written. If these look right, click <strong>AI Generate</strong> with the same
            selection to commit, then run the <code className="bg-purple-100 px-1 py-0.5 rounded">module → module_v2</code> copy SQL.
          </div>
        </div>
      )}

      {/* Selection limit note */}
      {selectedDocs.size > MAX_BULK_SELECT && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2 text-xs text-yellow-800">
          Maximum {MAX_BULK_SELECT} documents can be selected. Deselect some to select others.
        </div>
      )}

      {/* Document list */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-sm font-semibold flex items-center gap-1.5">
            <BookOpen size={14} />
            Documents ({filteredDocs.length})
          </h2>
        </div>

        {loading ? (
          <div className="p-6 text-center">
            <Loader2 size={20} className="text-gray-400 animate-spin mx-auto" />
            <p className="text-xs text-gray-500 mt-1.5">Loading documents...</p>
          </div>
        ) : filteredDocs.length === 0 ? (
          <div className="p-6 text-center text-gray-500 text-xs">
            {searchQuery || colFilterFilename || colFilterCategory
              ? 'No documents match your filters.'
              : 'No documents found.'}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  {/* Sort headers */}
                  <tr className="border-b border-gray-200 text-left text-gray-500">
                    <th className="px-3 py-2 font-medium w-8">
                      <input
                        type="checkbox"
                        checked={paginatedDocs.length > 0 && paginatedDocs.every((d) => selectedDocs.has(docKey(d)))}
                        onChange={toggleAllOnPage}
                        className="rounded border-gray-300"
                        disabled={generating}
                      />
                    </th>
                    <th
                      className="px-3 py-2 font-medium cursor-pointer hover:text-gray-900 select-none"
                      onClick={() => toggleSort('filename')}
                    >
                      <span className="inline-flex items-center gap-1">Filename <SortIcon field="filename" /></span>
                    </th>
                    <th
                      className="px-3 py-2 font-medium cursor-pointer hover:text-gray-900 select-none"
                      onClick={() => toggleSort('category')}
                    >
                      <span className="inline-flex items-center gap-1">Category <SortIcon field="category" /></span>
                    </th>
                    <th
                      className="px-3 py-2 font-medium cursor-pointer hover:text-gray-900 select-none"
                      onClick={() => toggleSort('frontmatter')}
                    >
                      <span className="inline-flex items-center gap-1">Frontmatter <SortIcon field="frontmatter" /></span>
                    </th>
                    <th
                      className="px-3 py-2 font-medium cursor-pointer hover:text-gray-900 select-none"
                      onClick={() => toggleSort('summary')}
                    >
                      <span className="inline-flex items-center gap-1">Summary Preview <SortIcon field="summary" /></span>
                    </th>
                    <th className="px-3 py-2 font-medium text-right">Action</th>
                  </tr>
                  {/* Column filter row */}
                  <tr className="border-b border-gray-100 bg-gray-50/50">
                    <th className="px-3 py-1" />
                    <th className="px-3 py-1">
                      <input
                        type="text"
                        value={colFilterFilename}
                        onChange={(e) => setColFilterFilename(e.target.value)}
                        placeholder="Filter..."
                        className="w-full text-[10px] border border-gray-200 rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-500 font-normal"
                      />
                    </th>
                    <th className="px-3 py-1">
                      <input
                        type="text"
                        value={colFilterCategory}
                        onChange={(e) => setColFilterCategory(e.target.value)}
                        placeholder="Filter..."
                        className="w-full text-[10px] border border-gray-200 rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-500 font-normal"
                      />
                    </th>
                    <th className="px-3 py-1" />
                    <th className="px-3 py-1" />
                    <th className="px-3 py-1" />
                  </tr>
                </thead>
                <tbody>
                  {paginatedDocs.map((doc) => {
                    const key = docKey(doc);
                    return (
                      <tr
                        key={key}
                        className={`border-b border-gray-100 hover:bg-gray-50 cursor-pointer ${
                          selectedDocs.has(key) ? 'bg-purple-50' : ''
                        }`}
                        onClick={() => openEditor(doc)}
                      >
                        <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selectedDocs.has(key)}
                            onChange={() => toggleDoc(key)}
                            className="rounded border-gray-300"
                            disabled={generating || (!selectedDocs.has(key) && selectedDocs.size >= MAX_BULK_SELECT)}
                          />
                        </td>
                        <td className="px-3 py-2 font-medium text-gray-900 max-w-xs truncate">
                          {doc.filename}
                        </td>
                        <td className="px-3 py-2 text-gray-600">
                          {doc.category?.replace(/_/g, ' ').replace(/-/g, ' ') || '-'}
                        </td>
                        <td className="px-3 py-2">
                          {!doc.frontmatter ? (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-red-100 text-red-700">
                              <XCircle size={10} />
                              Missing
                            </span>
                          ) : (doc.frontmatter.summary?.includes('Uploaded document') || doc.frontmatter.summary?.includes('pending')) ? (
                            // Placeholder: auto-created but not manually filled in
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-yellow-100 text-yellow-700">
                              <AlertTriangle size={10} />
                              Placeholder
                            </span>
                          ) : (
                            // Complete: manually curated with real metadata
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-green-100 text-green-700">
                              <CheckCircle size={10} />
                              Complete
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-gray-500 max-w-sm truncate">
                          {doc.frontmatter?.summary
                            ? doc.frontmatter.summary.length > 80
                              ? doc.frontmatter.summary.slice(0, 80) + '...'
                              : doc.frontmatter.summary
                            : '-'}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditor(doc);
                            }}
                            className="px-2 py-0.5 text-blue-600 hover:bg-blue-50 rounded text-xs transition-colors"
                          >
                            {doc.frontmatter ? 'Edit' : 'Add'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-4 py-2 border-t border-gray-200 flex items-center justify-between bg-gray-50">
                <span className="text-xs text-gray-500">
                  Showing {currentPage * PAGE_SIZE + 1}–
                  {Math.min((currentPage + 1) * PAGE_SIZE, filteredDocs.length)} of{' '}
                  {filteredDocs.length}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                    disabled={currentPage === 0}
                    className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:bg-gray-200 rounded disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft size={13} />
                    Prev
                  </button>
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages - 1, p + 1))}
                    disabled={currentPage >= totalPages - 1}
                    className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:bg-gray-200 rounded disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                    <ChevronRight size={13} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Editor modal */}
      {editingDoc && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-gray-900 truncate">
                  {editingDoc.frontmatter ? 'Edit' : 'Add'} Frontmatter
                </h3>
                <p className="text-xs text-gray-500 truncate mt-0.5">{editingDoc.filename}</p>
              </div>
              <button
                onClick={closeEditor}
                className="p-1 hover:bg-gray-100 rounded transition-colors flex-shrink-0"
              >
                <X size={16} className="text-gray-500" />
              </button>
            </div>

            {/* Form */}
            <div className="px-5 py-4 space-y-4">
              {/* Summary */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Summary <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formSummary}
                  onChange={(e) => setFormSummary(e.target.value)}
                  rows={4}
                  placeholder="Describe what this document covers in 2-3 sentences. This improves search retrieval and verification accuracy."
                  className="w-full text-xs border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
                <p className="text-[10px] text-gray-400 mt-1">
                  {formSummary.length} characters (min 10)
                </p>
              </div>

              {/* Module override */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Module Override{' '}
                  <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={formModule}
                  onChange={(e) => setFormModule(e.target.value)}
                  placeholder="e.g. Booking, Payments, Terminal Operations"
                  className="w-full text-xs border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-[10px] text-gray-400 mt-1">
                  Only set this if auto-detection assigns the wrong module
                </p>
              </div>

              {/* Feature override */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Feature Override{' '}
                  <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={formFeature}
                  onChange={(e) => setFormFeature(e.target.value)}
                  placeholder="e.g. Wire Transfer, Standard Booking, Bill of Lading"
                  className="w-full text-xs border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-[10px] text-gray-400 mt-1">
                  Only set this if auto-detection assigns the wrong feature
                </p>
              </div>

              {/* Metadata */}
              <div className="bg-gray-50 rounded-lg px-3 py-2 text-[10px] text-gray-500 space-y-0.5">
                <div>
                  <strong>Category:</strong> {editingDoc.category?.replace(/_/g, ' ') || '-'}
                </div>
                <div>
                  <strong>Storage Path:</strong> {editingDoc.gcs_path || '-'}
                </div>
                {editingDoc.frontmatter?.updated_at && (
                  <div>
                    <strong>Last Updated:</strong>{' '}
                    {new Date(editingDoc.frontmatter.updated_at).toLocaleString()}
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="px-5 py-3 border-t border-gray-200 flex items-center justify-between">
              <div>
                {editingDoc.frontmatter && (
                  <button
                    onClick={handleDelete}
                    disabled={saving}
                    className="flex items-center gap-1 px-3 py-1.5 text-red-600 hover:bg-red-50 rounded-md text-xs transition-colors disabled:opacity-50"
                  >
                    <Trash2 size={12} />
                    Remove
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={closeEditor}
                  className="px-3 py-1.5 text-gray-600 hover:bg-gray-100 rounded-md text-xs transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => editingDoc && handleSingleGenerate(editingDoc)}
                  disabled={generatingSingle || saving}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 text-white rounded-md text-xs font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Generate frontmatter using Claude"
                >
                  {generatingSingle ? (
                    <Loader2 size={12} className="animate-spin" />
                  ) : (
                    <Sparkles size={12} />
                  )}
                  {generatingSingle ? 'Generating...' : 'AI Generate'}
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving || generatingSingle || formSummary.trim().length < 10}
                  className="flex items-center gap-1.5 px-4 py-1.5 bg-blue-600 text-white rounded-md text-xs font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {saving ? (
                    <Loader2 size={12} className="animate-spin" />
                  ) : (
                    <Save size={12} />
                  )}
                  {saving ? 'Saving...' : 'Save Frontmatter'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
